
import { Link } from 'react-router-dom'

const pricing = [
    {
        name: 'Hobby',
        price: '0',
        description: 'Perfect for side projects and prototypes.',
        features: [
            '1 Concurrent Build',
            '512MB RAM Limit',
            'Scale to Zero',
            'Community Support',
            '100GB Bandwidth',
            '1 Project',
        ],
        cta: 'Start for Free',
        highlight: false,
    },
    {
        name: 'Pro',
        price: '29',
        description: 'For growing teams and production apps.',
        features: [
            '5 Concurrent Builds',
            '2GB RAM Limit',
            'Keep Warm Instances',
            'Priority Email Support',
            '1TB Bandwidth',
            'Unlimited Projects',
            'Team Management',
        ],
        cta: 'Upgrade to Pro',
        highlight: true,
    },
    {
        name: 'Enterprise',
        price: 'Contact',
        description: 'Custom solutions for large scale needs.',
        features: [
            'Unlimited Concurrent Builds',
            'Custom RAM/CPU',
            'Global CDN Management',
            'Dedicated Success Manager',
            'Custom Contracts',
            'SLA Guarantees',
            'SSO & Audit Logs',
        ],
        cta: 'Contact Sales',
        highlight: false,
    },
]

const faqs = [
    {
        q: 'Can I set a spending limit?',
        a: 'Yes, you can configure soft and hard limits on your billing settings page to prevent unexpected overages.',
    },
    {
        q: 'What happens if I exceed my limits?',
        a: 'For free tier users, services may be paused. For Pro users, we charge standard overage rates for bandwidth and compute time.',
    },
    {
        q: 'Do you support custom domains on the free tier?',
        a: 'Yes! You can attach up to 3 custom domains to your projects on the Hobby plan.',
    },
    {
        q: 'How does "Scale to Zero" work?',
        a: 'If your application receives no traffic for a set period, we spin down your container to save resources. The next request might take 1-2s longer (cold start) as we spin it back up.',
    },
]

const Pricing = () => {
    return (
        <div className="pb-24">
            <section className="mx-auto w-full max-w-6xl px-6 py-20 text-center">
                <div className="reveal">
                    <p className="text-sm font-semibold uppercase tracking-widest text-emerald-600">
                        Pricing
                    </p>
                    <h1 className="mt-4 text-4xl font-bold leading-tight tracking-tight text-slate-900 md:text-6xl">
                        Predictable costs. <br />
                        <span className="text-slate-500">No surprises.</span>
                    </h1>
                    <p className="mx-auto mt-6 max-w-2xl text-lg text-slate-600">
                        Start building for free. Upgrade as you scale. We charge based on resource usage, not seat count.
                    </p>
                </div>
            </section>

            <section className="mx-auto w-full max-w-7xl px-6">
                <div className="grid gap-8 lg:grid-cols-3">
                    {pricing.map((tier, index) => (
                        <div
                            key={tier.name}
                            className={`relative flex flex-col rounded-3xl border p-8 transition-transform hover:-translate-y-1 reveal ${tier.highlight
                                    ? 'border-slate-900 bg-slate-900 text-white shadow-2xl'
                                    : 'border-slate-200 bg-white text-slate-900 shadow-sm'
                                }`}
                            style={{ animationDelay: `${index * 100}ms` }}
                        >
                            {tier.highlight && (
                                <div className="absolute -top-4 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-r from-emerald-500 to-emerald-400 px-4 py-1 text-xs font-bold uppercase tracking-wider text-white shadow-sm">
                                    Most Popular
                                </div>
                            )}
                            <div className="mb-4">
                                <h3 className="text-xl font-bold">{tier.name}</h3>
                                <p className={`mt-2 text-sm ${tier.highlight ? 'text-slate-300' : 'text-slate-500'}`}>
                                    {tier.description}
                                </p>
                            </div>
                            <div className="mb-8 flex items-baseline">
                                <span className="text-4xl font-bold tracking-tight">
                                    {tier.price === 'Contact' ? 'Custom' : `$${tier.price}`}
                                </span>
                                {tier.price !== 'Contact' && <span className={`ml-2 text-sm font-medium ${tier.highlight ? 'text-slate-400' : 'text-slate-500'}`}>/month</span>}
                            </div>
                            <ul className="mb-8 flex-1 space-y-4 text-sm">
                                {tier.features.map((feature) => (
                                    <li key={feature} className="flex items-center gap-3">
                                        <svg className={`h-5 w-5 ${tier.highlight ? 'text-emerald-400' : 'text-emerald-500'}`} viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                        {feature}
                                    </li>
                                ))}
                            </ul>
                            <button
                                className={`w-full rounded-full py-3 text-sm font-bold transition-colors ${tier.highlight
                                        ? 'bg-white text-slate-900 hover:bg-slate-100'
                                        : 'border border-slate-200 bg-white text-slate-900 hover:border-slate-300 hover:bg-slate-50'
                                    }`}
                            >
                                {tier.cta}
                            </button>
                        </div>
                    ))}
                </div>
            </section>

            {/* Comparison Table */}
            <section className="mx-auto mt-32 w-full max-w-5xl px-6">
                <h2 className="text-2xl font-bold text-slate-900">Feature Comparison</h2>
                <div className="mt-8 overflow-hidden rounded-xl border border-slate-200 shadow-sm">
                    <table className="w-full text-left text-sm">
                        <thead className="bg-slate-50 text-slate-900">
                            <tr>
                                <th className="p-4 font-bold">Feature</th>
                                <th className="p-4 font-medium text-slate-500">Hobby</th>
                                <th className="p-4 font-medium text-slate-500">Pro</th>
                                <th className="p-4 font-medium text-slate-500">Enterprise</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 bg-white">
                            {[
                                ['Projects', '1', 'Unlimited', 'Unlimited'],
                                ['Members', 'Hobby-only', 'Unlimited', 'Unlimited'],
                                ['Build Minutes', '100/mo', '1,000/mo', 'Custom'],
                                ['Bandwidth', '100 GB', '1 TB', 'Custom'],
                                ['DDoS Protection', 'Basic', 'Standard', 'Advanced'],
                                ['Support', 'Community', 'Email (24h)', 'Priority (1h)'],
                                ['SLA', '-', '99.9%', '99.99%'],
                            ].map(([feature, hobby, pro, enterprise], idx) => (
                                <tr key={feature} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                                    <td className="p-4 font-medium text-slate-900">{feature}</td>
                                    <td className="p-4 text-slate-600">{hobby}</td>
                                    <td className="p-4 text-emerald-700 font-medium">{pro}</td>
                                    <td className="p-4 text-slate-600">{enterprise}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            {/* FAQ */}
            <section className="mx-auto mt-32 w-full max-w-4xl px-6">
                <h2 className="mb-12 text-center text-3xl font-bold text-slate-900">
                    Frequently Asked Questions
                </h2>
                <div className="grid gap-8 md:grid-cols-2">
                    {faqs.map((faq) => (
                        <div key={faq.q} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                            <h3 className="text-lg font-bold text-slate-900">{faq.q}</h3>
                            <p className="mt-3 text-slate-600 leading-relaxed">{faq.a}</p>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    )
}

export default Pricing
